import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';

import { TextField, ToggleField } from '~/components/form';
import { Button, Chip, RemoveRowButton } from '~/components/ui';

import { OPERATOR_LABELS } from '~/components/Constants/featureFlagsConstants';
import { Select } from '~/components/pages/feature-flags/common';
import { ValueToggles } from '~/components/pages/feature-flags/commonUi';
import type {
  Attribute,
  AudienceConfig,
  Condition,
  Operator,
  Product,
  Rule,
  UpdateEnvConfig,
} from '~/components/pages/feature-flags/types';

interface AudienceEditorProps<T extends AudienceConfig> {
  /** Prefix for every id and test id, so several editors can share a page. */
  idPrefix: string;
  value: T;
  onChange: (next: T) => void;
  attributes: Attribute[];
  products: Product[];
  /** What a rule serves, as the whole phrase: flags On / Off, updates Display / Hide, submissions Allow / Block. */
  labels?: { on: string; off: string };
  /** Index of the rule the live tester says decided the result. */
  decidingRule?: number | null;
  /** Rendered beside "Add rule" (flags: Copy rules). */
  ruleActions?: ReactNode;
}

const FLAG_LABELS = { on: 'On', off: 'Off' };

// "Display" → "Then display", "On" → "Then on".
const then = (label: string) => `Then ${label.charAt(0).toLowerCase()}${label.slice(1)}`;

/**
 * The ordered rules x conditions editor and the "when no rule matches" default, shared by flags,
 * product updates and a product's submission policy. The caller owns the enabled status.
 */
export const AudienceEditor = <T extends AudienceConfig>({
  idPrefix,
  value,
  onChange,
  attributes,
  products,
  labels = FLAG_LABELS,
  decidingRule,
  ruleActions,
}: AudienceEditorProps<T>) => {
  const patch = (next: Partial<UpdateEnvConfig>) => onChange({ ...value, ...next } as T);
  const { rules } = value;
  const setRules = (next: Rule[]) => patch({ rules: next });
  const editRule = (index: number, next: Rule) =>
    setRules(rules.map((r, i) => (i === index ? next : r)));
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= rules.length) return;
    const next = [...rules];
    [next[index], next[target]] = [next[target], next[index]];
    setRules(next);
  };

  const productOptions = products.map((p) => ({ value: p.key, label: p.name }));
  const attributeOf = (key: string) => attributes.find((a) => a.key === key);
  const operatorsFor = (key: string, current: Operator): Operator[] => {
    const attribute = attributeOf(key);
    if (!attribute) return [current];
    return key === 'product'
      ? attribute.operators.filter((op) => op !== 'endsWith')
      : attribute.operators;
  };
  // A boolean condition is "is true" or "is false": op "in" with one value, picked in the operator select.
  const isBoolean = (key: string) => attributeOf(key)?.type === 'boolean';
  const conditionFor = (attr: string, op: Operator): Condition => {
    if (isBoolean(attr)) return { attr, op: 'in', values: ['true'] };
    const allowed = operatorsFor(attr, op);
    return { attr, values: [], op: allowed.includes(op) ? op : (allowed[0] ?? 'in') };
  };
  const newCondition = (): Condition => conditionFor(attributes[0]?.key ?? '', 'in');

  const attributeOptions = (current: string) => [
    ...(attributeOf(current)
      ? []
      : [{ value: current, label: `${current || 'Choose attribute'} (removed)` }]),
    ...attributes.map((a) => ({ value: a.key, label: a.key })),
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Rules run top to bottom. The first rule where every condition matches decides the value.
      </p>

      {rules.length === 0 && (
        <p className="rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">
          No rules. Everyone gets the default. Add a rule to target customers, users or products.
        </p>
      )}

      {rules.map((rule, index) => {
        const num = index + 1;
        const decides = decidingRule === index;
        return (
          <div
            key={index}
            data-testid={`${idPrefix}-rule-${num}`}
            className={`space-y-4 rounded-lg border p-4 ${decides ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10' : 'border-gray-200 dark:border-gray-800'}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                  Rule {num}
                </span>
                {decides && <Chip label="Decides the test result" tone="brand" />}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  ariaLabel={`Move rule ${num} up`}
                  title={`Move rule ${num} up`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  testId={`${idPrefix}-rule-${num}-up`}
                >
                  <ChevronUp className="size-4" aria-hidden="true" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  ariaLabel={`Move rule ${num} down`}
                  title={`Move rule ${num} down`}
                  disabled={index === rules.length - 1}
                  onClick={() => move(index, 1)}
                  testId={`${idPrefix}-rule-${num}-down`}
                >
                  <ChevronDown className="size-4" aria-hidden="true" />
                </Button>
                <RemoveRowButton
                  label={`Remove rule ${num}`}
                  onClick={() => setRules(rules.filter((_, i) => i !== index))}
                  testId={`${idPrefix}-rule-${num}-delete`}
                />
              </div>
            </div>

            {rule.conditions.map((condition, k) => {
              const cid = `${idPrefix}-r${num}-c${k + 1}`;
              const attribute = attributeOf(condition.attr);
              const change = (next: Condition) =>
                editRule(index, {
                  ...rule,
                  conditions: rule.conditions.map((c, i) => (i === k ? next : c)),
                });
              const ops = operatorsFor(condition.attr, condition.op);
              const enumOptions =
                attribute?.type === 'enum'
                  ? (attribute.allowedValues ?? []).map((v) => ({ value: v, label: v }))
                  : null;
              return (
                <div key={k} className="space-y-2">
                  {k > 0 && (
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      and
                    </span>
                  )}
                  <div className="flex items-start gap-2">
                    <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
                      <Select
                        id={`${cid}-attr`}
                        label={`Rule ${num} condition ${k + 1} attribute`}
                        value={condition.attr}
                        options={attributeOptions(condition.attr)}
                        onChange={(attr) => change(conditionFor(attr, condition.op))}
                      />
                      {isBoolean(condition.attr) ? (
                        <Select
                          id={`${cid}-op`}
                          label={`Rule ${num} condition ${k + 1} operator`}
                          value={condition.values[0] ?? 'true'}
                          options={[
                            { value: 'true', label: 'is true' },
                            { value: 'false', label: 'is false' },
                          ]}
                          onChange={(v) => change({ ...condition, op: 'in', values: [v] })}
                        />
                      ) : (
                        <Select
                          id={`${cid}-op`}
                          label={`Rule ${num} condition ${k + 1} operator`}
                          value={condition.op}
                          options={ops.map((op) => ({ value: op, label: OPERATOR_LABELS[op] }))}
                          onChange={(op) => change({ ...condition, op: op as Operator })}
                        />
                      )}
                      {!isBoolean(condition.attr) && (
                        <div className="sm:col-span-2">
                          {condition.attr === 'product' ? (
                            <ValueToggles
                              idPrefix={`${cid}-product`}
                              label="Products"
                              options={productOptions}
                              value={condition.values}
                              onChange={(values) => change({ ...condition, values })}
                              emptyText="Create a product before targeting by product."
                            />
                          ) : enumOptions ? (
                            <ValueToggles
                              idPrefix={`${cid}-value`}
                              label="Values"
                              options={enumOptions}
                              value={condition.values}
                              onChange={(values) => change({ ...condition, values })}
                              emptyText="Add allowed values on the Attributes page first."
                            />
                          ) : (
                            <TextField
                              id={`${cid}-values`}
                              testId={`${cid}-values`}
                              label="Values"
                              code
                              value={condition.values.join(',')}
                              onChange={(v) => change({ ...condition, values: v.split(',') })}
                              placeholder={
                                condition.op === 'endsWith' ? '@backatyou.com' : 'value1, value2'
                              }
                              helperText="Separate several values with commas."
                            />
                          )}
                        </div>
                      )}
                    </div>
                    <div className="pt-7">
                      <RemoveRowButton
                        label={`Remove rule ${num} condition ${k + 1}`}
                        onClick={() =>
                          editRule(index, {
                            ...rule,
                            conditions: rule.conditions.filter((_, i) => i !== k),
                          })
                        }
                        testId={`${cid}-remove`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            {rule.conditions.length === 0 && (
              <p className="text-sm text-warning-600 dark:text-warning-400">
                Add a condition. A rule with no conditions never matches and cannot be saved.
              </p>
            )}

            <Button
              variant="outline"
              startIcon={<Plus className="size-4" aria-hidden="true" />}
              disabled={attributes.length === 0}
              onClick={() =>
                editRule(index, { ...rule, conditions: [...rule.conditions, newCondition()] })
              }
              testId={`${idPrefix}-rule-${num}-add-condition`}
            >
              Add condition
            </Button>

            <ToggleField
              id={`${idPrefix}-rule-${num}-value`}
              testId={`${idPrefix}-rule-${num}-value`}
              inline
              text={then(rule.value ? labels.on : labels.off)}
              checked={rule.value}
              onChange={(v) => editRule(index, { ...rule, value: v })}
            />
          </div>
        );
      })}

      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          startIcon={<Plus className="size-4" aria-hidden="true" />}
          disabled={attributes.length === 0}
          onClick={() => setRules([...rules, { conditions: [newCondition()], value: true }])}
          testId={`${idPrefix}-add-rule`}
        >
          Add rule
        </Button>
        {ruleActions}
      </div>
      {attributes.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Add targeting attributes on the Attributes page before creating rules.
        </p>
      )}

      <ToggleField
        id={`${idPrefix}-default`}
        testId={`${idPrefix}-default`}
        label="When no rule matches"
        text={value.defaultValue ? labels.on : labels.off}
        checked={value.defaultValue}
        onChange={(defaultValue) => patch({ defaultValue })}
      />
    </div>
  );
};

export default AudienceEditor;
